import { useLazyQuery } from '@apollo/client'
import { BoxLockVariationType } from '@jusbrasil-web/shared-apollo-client/types'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useContext, useEffect, useRef } from 'react'

import { BoxLockQueryQuery, BoxLockQueryQueryVariables } from './__generated__/query.graphql'
import {
  BoxLockContext,
  CaseTrackingBgCheckFromBgCheckOffersLock,
  CaseTrackingBGCheckFromLawsuitOffersLock,
  CustomPlanLock,
  DoctrineUnloggedLock,
  DynamicMultipleOffersSwitchFrequencyLock,
  InconsistentPlanLock,
  LawsuitNameMonitoringLock,
  LawsuitTwoOffersLock,
  LimitOverflowLock,
  LinkedAccountLock,
  OneOfferLock,
  PayPerViewLock,
  PixUpgradeLock,
  PreLoadingModal,
  ThirdPartyBackgroundCheckLock,
  UpdatePaymentInfoLock,
  Upgrade2StepCurrentLayoutLock,
} from './components'
import { useRegisterLockEvent } from './hooks'
import { BoxLockQuery } from './query'
import { BoxLockWithProviderProps, LockEventName } from './types'

// TODO: Fix yearly experiment behavior of sending 4 events on render. Before rollout, we need to think a better approach for this logic @augusto
// The ideia is let the component itself handle with lock events.
export const variantEventsBlockedOnProvider: BoxLockVariationType[] = [
  BoxLockVariationType.LawsuitTwoOffersLock,
  BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyLock,
  BoxLockVariationType.Upgrade_2StepCurrentLayoutLock,
]

export function BoxLockWithProvider(props: BoxLockWithProviderProps) {
  const { appId, featureCode, fromComponent, adminOptions, boxOptions, pageMetadata, setContext } =
    props
  const [fetchQuery, { data, loading }] = useLazyQuery<
    BoxLockQueryQuery,
    BoxLockQueryQueryVariables
  >(BoxLockQuery, {
    variables: {
      appId,
      featureCode,
      fromComponent,
      adminOptions,
      boxOptions,
      pageMetadata,
    },
  })

  const context = useContext(BoxLockContext)
  const { open, prefetch, setStatus, componentType } = context

  const isSuccessModalOpen =
    context.componentType === 'modal' && 'isSuccessModalOpen' in context
      ? context.isSuccessModalOpen
      : false

  const { registerLockEvent } = useRegisterLockEvent()
  const hasReportedUnrecognizedLock = useRef(false)

  useEffect(() => {
    if (context.componentType === 'modal') {
      setContext?.(context)
    }
  }, [context, setContext])

  useEffect(() => {
    if (prefetch) {
      fetchQuery()
    }
  }, [fetchQuery, prefetch])

  const hasData = !!data
  useEffect(() => {
    if (open) {
      if (hasData) {
        setStatus('ready')
      } else {
        fetchQuery()
      }
    }
  }, [fetchQuery, hasData, open, setStatus])

  const { lockVariationType, offerLockData } = data?.root.boxLockViewer || {}
  const { viewer: queryViewer } = data?.root || {}
  const viewer =
    adminOptions?.simulateUnauthenticated && queryViewer?.user?.isAdmin ? null : queryViewer

  useEffect(() => {
    if (open && offerLockData) {
      const isEventSendingBlockedOnProvider =
        lockVariationType && variantEventsBlockedOnProvider.includes(lockVariationType)
      if (!isEventSendingBlockedOnProvider) {
        const eventProperties = offerLockData.body.cards.map((card) => card.lockEventProperties)
        registerLockEvent({
          eventName: LockEventName.LOCK_VIEW,
          eventProperties,
        })
      }
    }
  }, [offerLockData, open, lockVariationType, registerLockEvent])

  if (open && loading && componentType === 'modal') {
    return <PreLoadingModal />
  }

  if (isSuccessModalOpen || !open || !data || loading) {
    return null
  }

  const LockComponent = getLockComponent({
    lockVariationType,
    offerLockData,
    viewer,
    featureCode,
    componentType,
  })

  if (
    open &&
    hasData &&
    !LockComponent &&
    !hasReportedUnrecognizedLock.current &&
    !adminOptions?.simulateUnauthenticated
  ) {
    hasReportedUnrecognizedLock.current = true
    sendMetrics('Box.UnrecognizedLockType', viewer?.pid ? String(viewer.pid) : null, {
      app_id: appId,
      from_component: fromComponent,
      feature: featureCode,
      modal_type: lockVariationType,
      source: 'BoxLockWithProvider',
      resource: componentType,
    })
  }

  return LockComponent
}

type GetLockComponentProps = {
  lockVariationType: BoxLockVariationType | null | undefined
  viewer: BoxLockQueryQuery['root']['viewer'] | null | undefined
  offerLockData:
    | NonNullable<BoxLockQueryQuery['root']['boxLockViewer']>['offerLockData']
    | null
    | undefined
  featureCode: string
  componentType: string
}

const getLockComponent = ({
  lockVariationType,
  componentType,
  offerLockData,
  viewer,
  featureCode,
}: GetLockComponentProps) => {
  if (offerLockData && viewer) {
    if (componentType === 'modal') {
      switch (lockVariationType) {
        case BoxLockVariationType.UpdatePaymentInfoLock:
          return <UpdatePaymentInfoLock offerLockData={offerLockData} />
        case BoxLockVariationType.FeatureLockLinkedAccountLock:
          return <LinkedAccountLock offerLockData={offerLockData} featureCode={featureCode} />
        case BoxLockVariationType.InconsistentLock:
          return <InconsistentPlanLock offerLockData={offerLockData} />
        case BoxLockVariationType.LimitOverflowLock:
          return <LimitOverflowLock offerLockData={offerLockData} />
        case BoxLockVariationType.LawsuitOneOfferLock:
          return <OneOfferLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitTwoOffersLock:
          return <LawsuitTwoOffersLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitNameMonitoringLock:
          return <LawsuitNameMonitoringLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.ThirdPartyBackgroundCheckTwoOffersLock:
          return <ThirdPartyBackgroundCheckLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitPayPerViewLock:
          return <PayPerViewLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.PixUpgradeLock:
          return <PixUpgradeLock offerLockData={offerLockData} />
        case BoxLockVariationType.CustomPlanLock:
          return <CustomPlanLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.CaseTrackingBgCheckFromBgCheckOffersLock:
          return (
            <CaseTrackingBgCheckFromBgCheckOffersLock
              offerLockData={offerLockData}
              viewer={viewer}
              lockVariationType={lockVariationType}
            />
          )
        case BoxLockVariationType.CaseTrackingBgCheckFromLawsuitOffersLock:
          return (
            <CaseTrackingBGCheckFromLawsuitOffersLock
              offerLockData={offerLockData}
              viewer={viewer}
              lockVariationType={lockVariationType}
            />
          )
        case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyLock:
          return (
            <DynamicMultipleOffersSwitchFrequencyLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
        case BoxLockVariationType.Upgrade_2StepCurrentLayoutLock:
          return <Upgrade2StepCurrentLayoutLock offerLockData={offerLockData} viewer={viewer} />
      }
    } else if (componentType === 'paywall') {
      switch (lockVariationType) {
        case BoxLockVariationType.UpdatePaymentInfoLock:
          return <UpdatePaymentInfoLock offerLockData={offerLockData} />
        case BoxLockVariationType.FeatureLockLinkedAccountLock:
          return <LinkedAccountLock offerLockData={offerLockData} featureCode={featureCode} />
        case BoxLockVariationType.InconsistentLock:
          return <InconsistentPlanLock offerLockData={offerLockData} />
        case BoxLockVariationType.LimitOverflowLock:
          return <LimitOverflowLock offerLockData={offerLockData} />
        case BoxLockVariationType.PixUpgradeLock:
          return <PixUpgradeLock offerLockData={offerLockData} />
        case BoxLockVariationType.CustomPlanLock:
          return <CustomPlanLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyLock:
          return (
            <DynamicMultipleOffersSwitchFrequencyLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
        case BoxLockVariationType.Upgrade_2StepCurrentLayoutLock:
          return <Upgrade2StepCurrentLayoutLock offerLockData={offerLockData} viewer={viewer} />
      }
    }
  }

  if (offerLockData && !viewer) {
    switch (lockVariationType) {
      case BoxLockVariationType.DoctrineUnloggedLock:
        return <DoctrineUnloggedLock offerLockData={offerLockData} />
      case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyLock:
        return (
          <DynamicMultipleOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={null} />
        )
    }
  }

  return null
}
